import type { BrowserOptions } from '@sentry/react';
import {
    addBreadcrumb,
    captureException,
    init,
    logger,
    reactErrorHandler,
    reactRouterBrowserTracingIntegration,
    setContext,
    setTag,
    setUser,
    thirdPartyErrorFilterIntegration,
    wrapCreateBrowserRouter,
} from '@sentry/react';

import React from 'react';
import {
    createBrowserRouter,
    createRoutesFromChildren,
    matchRoutes,
    useLocation,
    useNavigationType,
} from 'react-router';

import { createLogError } from '@repo/utils';

import { getAppVersion, getSentryConfig } from '../utils/window.utils';

const getSentryEnvironment = (): string => {
    const hostname = globalThis.location.hostname;

    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return 'local';
    }

    const viteEnv = String(import.meta.env.VITE_ENV);
    return viteEnv === 'prod' ? 'production' : viteEnv;
};

/**
 * Returns the Sentry configuration based on the VITE_ENV.
 * @returns The Sentry configuration.
 */
const sentryConfig = (): BrowserOptions => {
    const viteEnv = String(import.meta.env.VITE_ENV);
    const config = getSentryConfig();
    const dsn = config.dsn;
    const tracesSampleRate = viteEnv === 'prod' ? 0.1 : 0.5;
    const environment = getSentryEnvironment();

    return {
        dsn,
        environment,
        tracesSampleRate,
        enabled: environment !== 'local',
        dataCollection: {
            cookies: true,
            httpHeaders: { request: true, response: true },
            httpBodies: ['incomingRequest', 'outgoingRequest', 'incomingResponse', 'outgoingResponse'],
            queryParams: true,
            genAI: { inputs: false, outputs: false },
        },
        enableLogs: true,
        release: getAppVersion(),
    };
};

const config = sentryConfig();
const hasValidDsn = typeof config.dsn === 'string' && config.dsn.length > 0;

if (hasValidDsn) {
    try {
        init({
            ...config,
            integrations: [
                reactRouterBrowserTracingIntegration({
                    useEffect: React.useEffect,
                    useLocation,
                    useNavigationType,
                    createRoutesFromChildren,
                    matchRoutes,
                }),
                thirdPartyErrorFilterIntegration({
                    filterKeys: ['asset-view'],
                    behaviour: 'drop-error-if-exclusively-contains-third-party-frames',
                }),
            ],
        });
    } catch (error) {
        // oxlint-disable-next-line no-console
        console.warn('Sentry initialization failed', error);
    }
} else {
    // oxlint-disable-next-line no-console
    console.warn('Sentry initialization skipped: missing or invalid DSN');
}

export const logError = createLogError({ captureException, setContext, setTag });

export const sentryCreateBrowserRouter = wrapCreateBrowserRouter(createBrowserRouter);

export const createSentryErrorHandler = (
    callback?: (error: unknown, errorInfo: { componentStack?: string }) => void
) => {
    const sentryHandler = reactErrorHandler(
        callback
            ? (error, errorInfo) => {
                  callback(error, errorInfo.componentStack ? { componentStack: errorInfo.componentStack } : {});
              }
            : undefined
    );

    return (
        error: unknown,
        errorInfo: { componentStack?: string | undefined; errorBoundary?: React.Component<unknown> | undefined }
    ) => {
        sentryHandler(error, {
            componentStack: errorInfo.componentStack ?? null,
        });
    };
};

export const Sentry = { addBreadcrumb, logger, setUser };
